import { useId, useRef, useState } from 'react';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import RotateLeftIcon from '@mui/icons-material/RotateLeft';
import { Divider, Menu, MenuItem, Stack, Typography } from '@mui/material';
import { ActionIconButton } from 'framework/ui/ui-builder';
import FilterChip from './FilterChip';
import ConfiguredFilterChip from './ConfiguredFilterChip';
import { filterBarButtonSx } from './styles';
import FilterEditorPopover from './FilterEditorPopover';
import type { FilterBarFilterConfig, FilterBarFiltersProps, FilterValue } from './contracts';
import { getFilterValue, isActiveFilterValue, setFilterValue } from './filterState';

const keyboardFocusedMenuItemSx = { '&.Mui-focusVisible': { backgroundColor: 'action.hover' } };

const FilterBarFilters = ({
  title,
  actions,
  value,
  onChange,
  onReset,
  filters,
  quickFilters,
  activeQuickFilterId,
  onQuickFilterSelect,
  ariaLabel = 'Open quick filters',
  disabled,
  sx,
}: FilterBarFiltersProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const filterButtonRef = useRef<HTMLButtonElement | null>(null);
  const [editorAnchorEl, setEditorAnchorEl] = useState<HTMLElement | null>(null);
  const [editedFilterId, setEditedFilterId] = useState<string | null>(null);
  const isOpen = Boolean(anchorEl);
  const menuId = useId();
  const editorId = useId();
  const updateFilterValue = (filter: FilterBarFilterConfig, nextValue: FilterValue) =>
    onChange(setFilterValue(value, filter, nextValue));
  const editableFilters = filters.filter(filter => filter.type !== 'empty');
  const hasEditableFilters = editableFilters.length > 0 || Boolean(quickFilters?.length);
  const activeFilters = filters.filter(filter => isActiveFilterValue(getFilterValue(value, filter)));
  const hasActiveEditableFilters = activeFilters.some(filter => filter.type !== 'empty');
  const activeQuickFilter = quickFilters?.find(filter => filter.id === activeQuickFilterId);
  const editedFilter = editableFilters.find(filter => filter.id === editedFilterId) ?? null;

  const openFilterEditor = (filter: FilterBarFilterConfig, target?: HTMLElement) => {
    setAnchorEl(null);
    setEditedFilterId(filter.id);
    setEditorAnchorEl(target ?? filterButtonRef.current);
  };

  const closeFilterEditor = () => {
    setEditorAnchorEl(null);
    setEditedFilterId(null);
  };

  return (
    <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap" useFlexGap sx={sx}>
      {title}
      {activeQuickFilter && (
        <Typography color="textSecondary" fontWeight={600} sx={{ whiteSpace: 'nowrap' }}>
          {activeQuickFilter.label}
        </Typography>
      )}
      {activeFilters.map(filter =>
        filter.type === 'empty' ? (
          <FilterChip
            key={filter.id}
            label={filter.label}
            value={filter.value}
            strict={filter.strict}
            hideArrow
            onClick={filter.onClick}
            onRemove={filter.onRemove}
          />
        ) : (
          <ConfiguredFilterChip
            key={filter.id}
            filter={filter}
            selectedValue={getFilterValue(value, filter)}
            expanded={editedFilterId === filter.id}
            controls={editedFilterId === filter.id ? editorId : undefined}
            onClick={event => openFilterEditor(filter, event.currentTarget)}
            onRemove={() => updateFilterValue(filter, null)}
          />
        ),
      )}
      {hasEditableFilters && (
        <ActionIconButton
          ref={filterButtonRef}
          label={ariaLabel}
          tooltip="Filters"
          aria-haspopup="menu"
          aria-expanded={isOpen}
          aria-controls={isOpen ? menuId : undefined}
          icon={<FilterAltIcon />}
          disabled={disabled}
          onClick={event => setAnchorEl(event.currentTarget)}
          size="large"
          variant="outlined"
          sx={filterBarButtonSx}
        />
      )}
      {onReset && hasActiveEditableFilters && (
        <ActionIconButton
          label="Reset filters"
          tooltip="Reset"
          icon={<RotateLeftIcon />}
          disabled={disabled}
          onClick={onReset}
          size="large"
          variant="outlined"
          sx={filterBarButtonSx}
        />
      )}
      <Menu
        open={isOpen}
        anchorEl={anchorEl}
        disableAutoFocusItem
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          list: { id: menuId, 'aria-label': 'Quick filters and filter options' },
          paper: { sx: { mt: 1, minWidth: 280, maxWidth: 360, py: 1 } },
        }}
      >
        {Boolean(quickFilters?.length) && (
          <MenuItem disabled role="presentation" sx={{ minHeight: 28, opacity: '1 !important', px: 2 }}>
            <Typography color="textSecondary" fontWeight={600} variant="caption">
              Quick filters
            </Typography>
          </MenuItem>
        )}
        {quickFilters?.map(quickFilter => (
          <MenuItem
            key={quickFilter.id}
            selected={quickFilter.id === activeQuickFilterId}
            disabled={quickFilter.disabled}
            sx={keyboardFocusedMenuItemSx}
            onClick={() => {
              onChange(quickFilter.state);
              onQuickFilterSelect?.(quickFilter);
              setAnchorEl(null);
            }}
          >
            {quickFilter.label}
          </MenuItem>
        ))}
        {Boolean(quickFilters?.length && editableFilters.length) && <Divider component="li" role="separator" />}
        {editableFilters.map(filter => (
          <MenuItem
            key={filter.id}
            sx={keyboardFocusedMenuItemSx}
            onClick={event => openFilterEditor(filter, event.currentTarget)}
          >
            {filter.label}
          </MenuItem>
        ))}
      </Menu>
      <FilterEditorPopover
        id={editorId}
        anchorEl={editorAnchorEl}
        filter={editedFilter}
        value={editedFilter ? getFilterValue(value, editedFilter) : null}
        onChange={nextValue => {
          if (editedFilter) updateFilterValue(editedFilter, nextValue);
        }}
        onClose={closeFilterEditor}
      />
      {actions?.map(action => (
        <ActionIconButton
          key={action.id}
          label={action.label}
          tooltip={action.label}
          icon={action.icon}
          disabled={disabled || action.disabled}
          onClick={action.onClick}
          size="medium"
          variant={action.variant ?? 'outlined'}
          sx={filterBarButtonSx}
        />
      ))}
    </Stack>
  );
};

export default FilterBarFilters;
